import React, { useEffect, useRef, useState } from 'react'
import { useToast } from "@/components/organisms/feedback/use-toast"
import useUpdateCooptation from '@/services/consultant-services/mutation/useUpdateCooptation';
import useConsultantCooptations from '@/services/admin-services/query/useConsultantCooptations';

let rowKeySequence = 0;
const createRow = (values = {}) => ({
    key: `cooptation-row-${rowKeySequence++}`,
    uuid: '',
    pourcent: '',
    duration: '',
    date: '',
    ...values,
});

const toRows = (cooptedBy = []) => cooptedBy.map((cooptation) => createRow({
    uuid: cooptation?.cooptant?.uuid || '',
    pourcent: cooptation?.percentage != null ? String(cooptation.percentage) : '',
    duration: cooptation?.monthsDuration != null ? String(cooptation.monthsDuration) : '',
    date: cooptation?.startDate ? String(cooptation.startDate).slice(0, 10) : '',
}));

const UseNewCooptation = ({ onSaved } = {}) => {
    const [rows, setRows] = useState([]);
    const [cooptingUUID, setcooptingUUID] = useState();
    const [openNewCooptation, setOpenNewCooptation] = React.useState(false);
    const [showErrorMessage, setShowErrorMessage] = useState('');
    const { toast } = useToast();
    const { setCooptation, loading: saving } = useUpdateCooptation();
    const { data: cooptations, loading: cooptationsLoading, error: cooptationsError } = useConsultantCooptations(openNewCooptation ? cooptingUUID : undefined);
    // Uuid du coopte dont les cooptants ont deja ete charges dans le formulaire.
    const initializedForRef = useRef(null);

    useEffect(() => {
        if (!openNewCooptation) {
            setRows([]);
            setcooptingUUID(undefined);
            setShowErrorMessage('');
            initializedForRef.current = null;
        }
    }, [openNewCooptation]);

    // Pre-remplit le formulaire avec les cooptants actuels, une seule fois par coopte selectionne.
    useEffect(() => {
        if (!cooptations || cooptations.uuid !== cooptingUUID || initializedForRef.current === cooptingUUID) return;
        initializedForRef.current = cooptingUUID;
        setRows(toRows(cooptations.cooptedBy || []));
    }, [cooptations, cooptingUUID]);

    const changeCoopted = (uuid) => {
        if (uuid === cooptingUUID) return;
        setShowErrorMessage('');
        setRows([]);
        initializedForRef.current = null;
        setcooptingUUID(uuid);
    };

    const addRow = () => setRows((currentRows) => [...currentRows, createRow()]);

    const updateRow = (index, lineData) => {
        setRows((currentRows) => currentRows.map((row, rowIndex) => (rowIndex === index ? { ...row, ...lineData } : row)));
    };

    const removeRow = (index) => {
        setRows((currentRows) => currentRows.filter((_, rowIndex) => rowIndex !== index));
    };

    const removeAllRows = () => setRows([]);

    function onCancel() {
        toast({
            variant: "warning",
            title: "error.discardChange",
            duration: 8000,
        });
        setOpenNewCooptation(false);
    }

    async function onSend() {
        if (!cooptingUUID) {
            setShowErrorMessage("missingRequired");
            return;
        }

        try {
            dataControl();
        } catch (error) {
            console.error("Error while processing data: ", error);
            return
        }

        // Un tableau vide supprime toutes les cooptations du coopte.
        const cooptedBy = rows.map(item => ({
            cooptantConsultantUuid: item.uuid,
            percentage: parseInt(item.pourcent, 10),
            startDate: item.date,
            monthsDuration: item.duration === '' ? null : parseInt(item.duration, 10)
        }));

        const { error } = await setCooptation(cooptingUUID, cooptedBy);
        if (error) {
            toast({
                variant: "error",
                title: "error.requestFailed",
                duration: 8000,
                description: error
            });
            return;
        }

        toast({
            variant: "success",
            title: cooptedBy.length === 0 ? "consultants.dashboard.newCooptation.deleteSuccess" : "success.changesSaved",
            duration: 1500,
        });
        onSaved && onSaved(cooptingUUID);
        setOpenNewCooptation(false);
    }

    function dataControl() {
        setShowErrorMessage("")
        let totalPercentage = 0;
        let names = new Set();
        rows.forEach(item => {
            if (!item.uuid || !item.pourcent || item.pourcent == 0 || !item.date) {
                setShowErrorMessage("missingRequired");
                throw new Error("missingRequired");
            }
            if (names?.has(item.uuid)) {
                setShowErrorMessage("mulitpleSimilarCoopting");
                throw new Error("mulitpleSimilarCoopting");
            } else {
                names.add(item.uuid);
            }
            totalPercentage += parseInt(item.pourcent, 10);
        });
        if (rows.length > 0 && totalPercentage !== 100) {
            setShowErrorMessage("totalOwnership");
            throw new Error("totalOwnership");
        }
    }

    const totalPercentage = rows.reduce((total, row) => total + (parseInt(row.pourcent, 10) || 0), 0);

    return {
        showErrorMessage,
        openNewCooptation,
        setOpenNewCooptation,
        cooptingUUID,
        changeCoopted,
        rows,
        addRow,
        updateRow,
        removeRow,
        removeAllRows,
        totalPercentage,
        cooptations,
        cooptationsLoading,
        cooptationsError,
        saving,
        onCancel,
        onSend,
        dataControl,
    }
}
export default UseNewCooptation;
